import {
	faArrowDown,
	faArrowRight,
	faArrowsUpDown,
	faEllipsisVertical,
	faEmptySet,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { JsonInputType } from "@tosize/shapes/src/lib/models/Input/JsonInputType";
import { SideName } from "@tosize/shapes/src/lib/models/Side/SideName";
import type { AvailableOnSide, Finish } from "@tosize/shapes/src/types/finish";
import {
	Button,
	NativeNumberInput,
	Popover,
	RadixSelect,
	RadixSelectItem,
	Typography,
	formatNumber,
} from "@tosize/ui";
import { produce } from "immer";
import {
	type Dispatch,
	Fragment,
	type PropsWithChildren,
	type SetStateAction,
	useMemo,
	useState,
} from "react";
import { useIntl } from "react-intl";
import { match } from "ts-pattern";
import { usePostDataContext } from "../../../context/PostDataContext";
import useProducts from "../../../hooks/useProducts";
import { getDefaultOptionForFinish } from "../../../hooks/useSelectedFinishes";
import useShapes from "../../../hooks/useShapes";
import { getWorkPieceDrillholes } from "./utils";

export const DIAMETERID = "diameter_";

type HolesType = {
	position: number;
	offset_x: number | null;
	offset_y: number | null;
	diameter: number | null;
	depth: number | null;
};

export function DrillHoles(props: Pick<DrillHoleProps, "finish" | "side">) {
	const { ...remainingProps } = props;

	const { productData } = useProducts();
	const { workpiece } = usePostDataContext();
	const workPieceHoles = getWorkPieceDrillholes({ workpiece, side: props.side });

	const defaultDiameter = 5;
	const defaultFinishOption =
		Object.values(props.finish.options).find(({ id }) => id === `diameter_${defaultDiameter}`) ??
		Object.values(props.finish.options).at(0);
	const defaultFields = defaultFinishOption?.constraints.fields
		? Object.values(defaultFinishOption.constraints.fields)
		: [];
	const defaultDepthField = defaultFields.find(({ name }) => name === "depth");
	const thickness = Number(productData?.thickness);

	const defaultHole = {
		position: workPieceHoles.length,
		offset_x: null,
		offset_y: null,
		diameter: defaultDiameter,
		depth: defaultDepthField?.max_depth ? Number(defaultDepthField?.max_depth) : thickness,
	} satisfies HolesType;

	const holes = workPieceHoles.concat(defaultHole);
	const lastPosition = holes.length - 1;
	const [current, setCurrent] = useState<number>(lastPosition);

	return (
		<Fragment key={holes.length}>
			{holes.map((hole, index) => (
				<DrillHole
					{...remainingProps}
					hole={hole}
					current={current}
					setCurrent={setCurrent}
					lastPosition={lastPosition}
					key={index}
				/>
			))}
		</Fragment>
	);
}

interface DrillHoleProps {
	finish: Finish;
	side: AvailableOnSide;
	hole: HolesType;
	current: number;
	setCurrent: Dispatch<SetStateAction<number>>;
	lastPosition: number;
}

function DrillHole(props: DrillHoleProps) {
	const { finish, side, hole, current, setCurrent, lastPosition } = props;

	const intl = useIntl();
	const { shape } = useShapes();
	const { productData } = useProducts();
	const { workpiece, setWorkpiece } = usePostDataContext();

	const maxWidth = Number(workpiece.shape_dimensions.width) / 10;
	const maxLength = Number(workpiece.shape_dimensions.length) / 10;
	const thickness = Number(productData?.thickness);

	const [localBoreHole, setLocalBoreHole] = useState<HolesType>(hole);

	const isOpen = current === localBoreHole.position;
	const isLast = localBoreHole.position === lastPosition;

	const positionLabel = localBoreHole.position + 1;

	const diameter = localBoreHole.diameter ? DIAMETERID + localBoreHole.diameter : undefined;

	const finishOption = diameter
		? Object.values(finish.options).find(({ id }) => id === diameter)
		: Object.values(finish.options).at(0);

	const fields = finishOption?.constraints.fields
		? Object.values(finishOption.constraints.fields)
		: [];

	const offsetX = fields.find(({ name }) => name === "offset_x");
	const offsetY = fields.find(({ name }) => name === "offset_y");
	const depth = fields.find(({ name }) => name === "depth");

	const maxDepth = depth?.max_depth ? Number(depth?.max_depth) : thickness;

	const maxOffsetX = match(side.sideCode)
		.with(
			SideName.Top,
			SideName.Bottom,
			SideName.Length1,
			SideName.Length2,

			() => ({
				label: shape?.inputs.find((i) => i.id === JsonInputType.Length)?.label,
				value: maxLength,
			}),
		)
		.with(SideName.Width1, SideName.Width2, () => ({
			label: shape?.inputs.find((i) => i.id === JsonInputType.Width)?.label,
			value: maxWidth,
		}))
		.otherwise(() => ({ label: "", value: 0 }));

	const maxOffsetY = match(side.sideCode)
		.with(SideName.Top, SideName.Bottom, () => ({
			label: shape?.inputs.find((i) => i.id === JsonInputType.Width)?.label,
			value: maxWidth,
		}))
		.with(SideName.Length1, SideName.Length2, SideName.Width1, SideName.Width2, () => ({
			label: intl.formatMessage({ defaultMessage: "Dikte", id: "qzkByd" }),
			value: maxDepth / 10,
		}))
		.otherwise(() => ({ label: "", value: 0 }));

	const isDisabled = useMemo(() => {
		const hasValues = Object.values(localBoreHole).every((hole) => hole !== null);

		if (hasValues) {
			const offsetXIsInValid = Number(localBoreHole.offset_x) > maxOffsetX.value;
			const offsetYIsInValid = Number(localBoreHole.offset_y) > maxOffsetY.value;

			if (!(offsetXIsInValid || offsetYIsInValid)) {
				return false;
			}
		}

		return true;
	}, [localBoreHole, maxOffsetX.value, maxOffsetY]);

	if (!isOpen && isLast) {
		return null;
	}

	return (
		<div className="flex w-full flex-col gap-4">
			{isOpen ? (
				<div className="grid w-full xs:grid-cols-2 gap-4">
					{offsetX && (
						<NativeNumberInput
							autoFocus
							title={offsetX.label}
							label={
								<span className="flex items-center gap-1">
									<FontAwesomeIcon icon={faArrowRight} fixedWidth />
									<Typography>{offsetX.label}</Typography>
									{!!offsetX.comment && (
										<Typography color="text-shades-dark" size="text-sm">
											- {offsetX.comment}
										</Typography>
									)}
								</span>
							}
							id={offsetX.name}
							decimals={1}
							surfix="cm"
							max={maxOffsetX.value}
							value={localBoreHole.offset_x ? Number(localBoreHole.offset_x) : undefined}
							valueChangeHandler={(value) =>
								setLocalBoreHole((prev) => ({ ...prev, offset_x: value }))
							}
							customErrorMessage={{
								too_big: intl.formatMessage(
									{
										defaultMessage:
											"De waarde van {title} mag niet groter zijn dan de waarde van de {label} van het paneel ({value} cm)",
										id: "5UnqQO",
									},
									{
										title: offsetX.label,
										label: maxOffsetX.label,
										value: maxOffsetX.value,
									},
								),
							}}
						/>
					)}
					{offsetY && (
						<NativeNumberInput
							title={offsetY.label}
							label={
								<span className="flex items-center gap-1">
									<FontAwesomeIcon icon={faArrowDown} fixedWidth />
									<Typography>{offsetY.label}</Typography>
									{!!offsetY.comment && (
										<Typography color="text-shades-dark" size="text-sm">
											- {offsetY.comment}
										</Typography>
									)}
								</span>
							}
							id={offsetY.name}
							decimals={1}
							surfix="cm"
							max={maxOffsetY.value}
							value={localBoreHole.offset_y ? Number(localBoreHole.offset_y) : undefined}
							valueChangeHandler={(value) =>
								setLocalBoreHole((prev) => ({ ...prev, offset_y: value }))
							}
							customErrorMessage={{
								too_big: intl.formatMessage(
									{
										defaultMessage:
											"De waarde van {title} mag niet groter zijn dan de waarde van de {label} van het paneel ({value} cm)",
										id: "5UnqQO",
									},
									{
										title: offsetY.label,
										label: maxOffsetY.label,
										value: maxOffsetY.value,
									},
								),
							}}
						/>
					)}

					<div className="flex flex-col">
						<RadixSelect
							className="grow"
							label={
								<span className="flex items-center gap-1">
									<FontAwesomeIcon icon={faEmptySet} fixedWidth />
									{intl.formatMessage({ defaultMessage: "Diameter", id: "9qyhXb" })}
								</span>
							}
							id="diameter"
							value={localBoreHole.diameter ? String(localBoreHole.diameter) : undefined}
							onValueChange={(value) =>
								setLocalBoreHole((prev) => ({
									...prev,
									diameter: Number(value),
									depth: maxDepth,
								}))
							}
						>
							{Object.entries(finish.options)
								.filter(([_, finish]) =>
									finish.constraints.available_on_sides?.includes(side.sideCode),
								)
								.map(([code, finish]) => (
									<RadixSelectItem key={code} value={code.replace(DIAMETERID, "")}>
										{finish.label}
									</RadixSelectItem>
								))}
						</RadixSelect>
					</div>

					{depth && (
						<div className="flex flex-col">
							<RadixSelect
								className="grow"
								label={
									<span className="flex items-center gap-1">
										<FontAwesomeIcon icon={faArrowsUpDown} fixedWidth />
										{depth.label}
									</span>
								}
								id={depth.name}
								value={localBoreHole.depth ? String(localBoreHole.depth) : undefined}
								onValueChange={(value) =>
									setLocalBoreHole((prev) => ({ ...prev, depth: Number(value) }))
								}
							>
								{Array.from({ length: maxDepth }).map((_, index) => (
									<RadixSelectItem key={index} value={String(index + 1)}>
										{formatNumber({
											lang: intl.locale,
											value: index + 1,
											style: "unit",
											unit: "millimeter",
										})}
										{maxOffsetY.value === maxWidth &&
											thickness === index + 1 &&
											` - ${intl.formatMessage({ defaultMessage: "Doorgaand", id: "9b6KzG" })}`}
									</RadixSelectItem>
								))}
							</RadixSelect>
						</div>
					)}

					<Button
						className="col-span-full"
						disabled={isDisabled}
						onClick={() => {
							setWorkpiece(
								produce((state) => {
									const current =
										state.finishes.side[side.sideCode]?.[side.finishCode]?.[localBoreHole.position];

									const optionParameters = {
										offset_x: Number(localBoreHole.offset_x) * 10,
										offset_y: Number(localBoreHole.offset_y) * 10,
										depth: Number(localBoreHole.depth),
									};

									if (current) {
										current.option = diameter ?? getDefaultOptionForFinish(finish);
										current.option_parameters = optionParameters;
									} else {
										state.finishes.side[side.sideCode] = {
											[side.finishCode]: [
												...(state.finishes.side[side.sideCode]?.[side.finishCode] ?? []),
												{
													option: diameter ?? getDefaultOptionForFinish(finish),
													option_parameters: optionParameters,
												},
											],
										};
									}
								}),
							);

							setCurrent(isLast ? lastPosition + 1 : lastPosition);
						}}
					>
						{isLast
							? `+ ${intl.formatMessage(
									{ defaultMessage: "Boorgat {label} toevoegen", id: "A0O/Dm" },
									{ label: positionLabel },
								)}`
							: intl.formatMessage(
									{ defaultMessage: "Boorgat {label} opslaan", id: "uKCNbh" },
									{ label: positionLabel },
								)}
					</Button>
				</div>
			) : (
				<ActiveDrillHole hole={localBoreHole}>
					<Popover
						color="shades-light"
						side="left"
						content={
							<>
								<Button
									color="danger"
									size="small"
									autoFocus
									onClick={() => {
										setWorkpiece(
											produce((state) => {
												const finishes = state.finishes.side[side.sideCode]?.[side.finishCode];

												if (finishes) {
													finishes.splice(hole.position, 1);
												}
											}),
										);

										setCurrent(lastPosition - 1);
									}}
								>
									{intl.formatMessage({ defaultMessage: "Verwijderen", id: "mcAe/q" })}
								</Button>

								<Button size="small" onClick={() => setCurrent(hole.position)}>
									{intl.formatMessage({ defaultMessage: "Bewerken", id: "/rVWo/" })}
								</Button>
							</>
						}
					>
						<button
							title={intl.formatMessage({ defaultMessage: "Instellingen", id: "ejUt3J" })}
							className="ml-auto flex h-[22px] w-[22px] appearance-none items-center justify-center rounded-full enter:bg-shades-medium-light enter:text-dark text-base text-shades-dark focus:outline-none disabled:cursor-not-allowed disabled:text-shades-medium motion-safe:transition-colors"
							type="button"
						>
							<FontAwesomeIcon icon={faEllipsisVertical} fixedWidth />
						</button>
					</Popover>
				</ActiveDrillHole>
			)}
		</div>
	);
}

export function ActiveDrillHole({
	hole,
	children,
}: PropsWithChildren<Pick<DrillHoleProps, "hole">>) {
	const intl = useIntl();

	return (
		<div className="-mx-4 flex items-center gap-4 bg-shades-light px-4 py-1">
			<div className="-ml-4 relative mr-[16px] bg-secondary py-1 pr-2 pl-4 text-sm text-white leading-none">
				<span className="relative z-1">{hole.position + 1}</span>
				<span className="absolute top-0 right-[-16px] bottom-0 w-[32px] rounded-full bg-secondary" />
			</div>
			<div className="grid grid-cols-2 xs:grid-cols-4 gap-x-2 gap-y-1 text-sm leading-none sm:gap-x-4 lg:gap-x-2 xl:gap-x-4">
				<div className="flex items-center gap-0.5">
					<FontAwesomeIcon icon={faArrowRight} fixedWidth className="text-shades-dark text-xs" />
					{formatNumber({
						lang: intl.locale,
						value: hole.offset_x ?? 0,
						style: "unit",
						unit: "centimeter",
					})}
				</div>
				<div className="flex items-center gap-0.5">
					<FontAwesomeIcon icon={faArrowDown} fixedWidth className="text-shades-dark text-xs" />
					{formatNumber({
						lang: intl.locale,
						value: hole.offset_y ?? 0,
						style: "unit",
						unit: "centimeter",
					})}
				</div>
				<div className="flex items-center gap-0.5">
					<FontAwesomeIcon icon={faEmptySet} fixedWidth className="text-shades-dark text-xs" />
					{formatNumber({
						lang: intl.locale,
						value: hole.diameter ?? 0,
						style: "unit",
						unit: "millimeter",
					})}
				</div>
				<div className="flex items-center gap-0.5">
					<FontAwesomeIcon icon={faArrowsUpDown} fixedWidth className="text-shades-dark text-xs" />
					{formatNumber({
						lang: intl.locale,
						value: hole.depth ?? 0,
						style: "unit",
						unit: "millimeter",
					})}
				</div>
			</div>
			{children}
		</div>
	);
}
